import { useEffect, useRef } from "react";
import {
  RouteQueryResponse,
  RouteLocation,
  RouteOptions,
} from "../redux/services/config";

interface RouteResult {
  currentData?: RouteQueryResponse;
  originalArgs?: { to: RouteLocation; options?: RouteOptions };
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
}

// RTK Query's `data` can belong to the previous arguments. Only cache a
// completed response for this selection, and only reuse it for a failed
// step-free request to the same destination.
export const useRouteResponse = (
  result: RouteResult,
  featureId: string | number | undefined,
  selectionVersion: number,
) => {
  const previous = useRef<{
    selection: string;
    route: RouteQueryResponse;
    stepFree: boolean;
  }>();
  const selection =
    featureId == null ? undefined : `${featureId}:${selectionVersion}`;
  const destination = result.originalArgs?.to;
  const matches =
    selection !== undefined &&
    destination &&
    "featureId" in destination &&
    destination.featureId === Number(featureId);
  const stepFree = result.originalArgs?.options?.routeModifier === "step_free";
  const successful =
    matches && !result.isFetching && result.isSuccess
      ? result.currentData
      : undefined;

  useEffect(() => {
    if (previous.current?.selection !== selection) previous.current = undefined;
    if (successful && selection) {
      previous.current = { selection, route: successful, stepFree };
    }
  }, [selection, successful, stepFree]);

  const fallback =
    matches &&
    !result.isFetching &&
    result.isError &&
    stepFree &&
    previous.current?.selection === selection
      ? previous.current
      : undefined;

  return {
    displayedRoute: successful ?? fallback?.route,
    displayedStepFree: successful ? stepFree : (fallback?.stepFree ?? false),
  };
};
